// Insurer payments (remittances) against claims, and debtors aging
// (docs/plans/phase2-private-insurance.md §3). A remittance is allocated to claims by hand
// or oldest first; what isn't allocated stays on it as unapplied money.

import type { Money } from '../money.ts';
import { addMoney, minMoney, subtractMoney, zeroMoney } from '../money.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { CalendarDay } from '../time.ts';
import { daysBetween } from '../time.ts';

export const REMITTANCE_METHODS = ['eft', 'cheque', 'mpesa', 'other'] as const;
export type RemittanceMethod = (typeof REMITTANCE_METHODS)[number];

export const REMITTANCE_METHOD_WORDS: Record<RemittanceMethod, string> = {
  eft: 'Bank transfer',
  cheque: 'Cheque',
  mpesa: 'M-Pesa',
  other: 'Other',
};

export interface OutstandingClaim {
  readonly id: string;
  /** When the claim went on an invoice to the insurer. */
  readonly invoicedAt: Date;
  readonly outstanding: Money;
}

export interface ClaimAllocation {
  readonly claimId: string;
  readonly amount: Money;
}

function oldestFirst(a: OutstandingClaim, b: OutstandingClaim): number {
  const byTime = a.invoicedAt.getTime() - b.invoicedAt.getTime();
  if (byTime !== 0) return byTime;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Pays the oldest invoiced claims first; the rest of the money is unapplied. */
export function allocateOldestFirst(
  amount: Money,
  claims: readonly OutstandingClaim[],
): { allocations: ClaimAllocation[]; unapplied: Money } {
  let left = amount;
  const allocations: ClaimAllocation[] = [];
  for (const claim of [...claims].sort(oldestFirst)) {
    if (left.cents <= 0n) break;
    if (claim.outstanding.cents <= 0n) continue;
    const take = minMoney(left, claim.outstanding);
    allocations.push({ claimId: claim.id, amount: take });
    left = subtractMoney(left, take);
  }
  return { allocations, unapplied: left };
}

export type AllocationErrorCode =
  | 'allocation.not_positive'
  | 'allocation.unknown_claim'
  | 'allocation.duplicate_claim'
  | 'allocation.exceeds_outstanding'
  | 'allocation.exceeds_remittance';

export const ALLOCATION_ERRORS: Record<AllocationErrorCode, string> = {
  'allocation.not_positive': 'Each amount must be more than zero',
  'allocation.unknown_claim': 'A claim isn’t open for this insurer',
  'allocation.duplicate_claim': 'A claim appears twice',
  'allocation.exceeds_outstanding': 'An amount is more than the claim has outstanding',
  'allocation.exceeds_remittance': 'The amounts add up to more than the payment has left',
};

/** Checks allocations a person chose; returns their total. */
export function checkAllocations(input: {
  readonly available: Money;
  readonly allocations: readonly ClaimAllocation[];
  readonly outstanding: ReadonlyMap<string, Money>;
}): Result<Money, AllocationErrorCode> {
  const seen = new Set<string>();
  let total = zeroMoney(input.available.currency);
  for (const a of input.allocations) {
    if (a.amount.cents <= 0n) return err('allocation.not_positive');
    const open = input.outstanding.get(a.claimId);
    if (open === undefined) return err('allocation.unknown_claim');
    if (seen.has(a.claimId)) return err('allocation.duplicate_claim');
    seen.add(a.claimId);
    if (a.amount.cents > open.cents) return err('allocation.exceeds_outstanding');
    total = addMoney(total, a.amount);
  }
  if (total.cents > input.available.cents) return err('allocation.exceeds_remittance');
  return ok(total);
}

// --- Aging -------------------------------------------------------------------------------

/** Bands in days since the invoice (plan B7, provisional: TODO(business)). */
export const DEBTOR_AGE_BANDS = ['0_30', '31_60', '61_90', '91_120', 'over_120'] as const;
export type DebtorAgeBand = (typeof DEBTOR_AGE_BANDS)[number];

export const DEBTOR_AGE_BAND_WORDS: Record<DebtorAgeBand, string> = {
  '0_30': '0 to 30 days',
  '31_60': '31 to 60 days',
  '61_90': '61 to 90 days',
  '91_120': '91 to 120 days',
  over_120: 'Over 120 days',
};

export function debtorAgeBand(invoicedOn: CalendarDay, today: CalendarDay): DebtorAgeBand {
  const days = Math.max(0, daysBetween(invoicedOn, today));
  if (days <= 30) return '0_30';
  if (days <= 60) return '31_60';
  if (days <= 90) return '61_90';
  if (days <= 120) return '91_120';
  return 'over_120';
}

export interface DebtorSummary {
  readonly bands: Record<DebtorAgeBand, Money>;
  readonly total: Money;
  readonly claimCount: number;
}

/** Outstanding per insurer, in age bands. Rows with nothing outstanding are skipped. */
export function summariseDebtors(
  rows: readonly { payerId: string; outstanding: Money; invoicedOn: CalendarDay }[],
  today: CalendarDay,
): Map<string, DebtorSummary> {
  const out = new Map<
    string,
    { bands: Record<DebtorAgeBand, Money>; total: Money; claimCount: number }
  >();
  for (const row of rows) {
    if (row.outstanding.cents <= 0n) continue;
    let entry = out.get(row.payerId);
    if (entry === undefined) {
      const zero = zeroMoney(row.outstanding.currency);
      entry = {
        bands: { '0_30': zero, '31_60': zero, '61_90': zero, '91_120': zero, over_120: zero },
        total: zero,
        claimCount: 0,
      };
      out.set(row.payerId, entry);
    }
    const band = debtorAgeBand(row.invoicedOn, today);
    entry.bands = { ...entry.bands, [band]: addMoney(entry.bands[band], row.outstanding) };
    entry.total = addMoney(entry.total, row.outstanding);
    entry.claimCount += 1;
  }
  return out;
}
